'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

import { financialDataService, type MarketData } from '@/lib/services/financialDataService';

interface TabData {
  id: string;
  label: string;
  icon: string;
}

interface MarketTabsProps {
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

// Symbol mappings for different categories
const CATEGORY_SYMBOLS: Record<string, string[]> = {
  tab_stocks: ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'META', 'NVDA', 'JPM'],
  tab_commodities: ['GC=F', 'SI=F', 'CL=F', 'NG=F'],
  tab_forex: ['EUR/USD', 'GBP/USD', 'USD/JPY', 'AUD/USD'],
  tab_crypto: ['BTCUSD', 'ETHUSD', 'BNBUSD', 'SOLUSD'],
};

export default function MarketTabs({ activeTab, onTabChange }: MarketTabsProps) {
  const [stocksData, setStocksData] = useState<MarketData[]>([]);
  const [commoditiesData, setCommoditiesData] = useState<MarketData[]>([]);
  const [forexData, setForexData] = useState<MarketData[]>([]);
  const [cryptoData, setCryptoData] = useState<MarketData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<'yahoofinance' | 'supabase'>('yahoofinance');

  const tabs: TabData[] = [
    { id: 'tab_stocks', label: 'Stocks', icon: 'ChartBarIcon' },
    { id: 'tab_commodities', label: 'Commodities', icon: 'CubeIcon' },
    { id: 'tab_forex', label: 'Forex', icon: 'CurrencyDollarIcon' },
    { id: 'tab_crypto', label: 'Crypto', icon: 'CircleStackIcon' },
  ];

  const fetchFromAPI = async (): Promise<Stock[] | null> => {
    try {
      const symbols = POPULAR_STOCKS.map((s) => s.symbol);
      
      // Try Finnhub first
      const adapter = new FinancialAPIAdapter('finnhub');
      let quotes = await adapter.getMultipleQuotes(symbols);
      
      // Fallback to Alpha Vantage if Finnhub fails
      if (quotes.size === 0) {
        console.log('Finnhub failed, trying Alpha Vantage...');
        adapter.setProvider('alphavantage');
        quotes = await adapter.getMultipleQuotes(symbols);
      }

      if (quotes.size === 0) {
        return null;
      }

      const stocksData: Stock[] = [];
      POPULAR_STOCKS.forEach((stock) => {
        const quote = quotes.get(stock.symbol);
        if (quote) {
          stocksData.push({
            symbol: stock.symbol,
            name: stock.name,
            price: quote.price,
            change: quote.change,
            changePercent: quote.changePercent,
            high: quote.high,
            low: quote.low,
            volume: quote.volume || 0,
            category: stock.category,
            updatedAt: new Date().toISOString(),
          });
        }
      });

      return stocksData.length > 0 ? stocksData : null;
    } catch (err) {
      console.error('Financial API error:', err);
      return null;
    }
  };

  const fetchFromSupabase = async (): Promise<MarketData> => {
    try {
      const [stocks, forex, crypto, commodities] = await Promise.all([
        financialDataService.getStocks(),
        financialDataService.getForex(),
        financialDataService.getCrypto(),
        financialDataService.getCommodities(),
      ]);

      return { stocks, forex, crypto, commodities };
    } catch (err) {
      console.error('Supabase fallback error:', err);
      return { stocks: [], forex: [], crypto: [], commodities: [] };
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Try Financial API first (Finnhub with Alpha Vantage fallback)
      const apiData = await fetchFromAPI();
      
      if (apiData && apiData.length > 0) {
        setStocks(apiData);
        setDataSource('finnhub');
        setError(null);
      } else {
        // Fallback to Supabase
        console.log('Falling back to Supabase data');
        const supabaseData = await fetchFromSupabase();
        setStocks(supabaseData);
        setDataSource('supabase');
        setError(null);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load market data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Auto-refresh every 45 seconds
    const refreshInterval = setInterval(() => {
      fetchData();
    }, 45000);

    return () => {
      clearInterval(refreshInterval);
    };
  }, []);

  const getCurrentData = () => {
    switch (activeTab) {
      case 'tab_stocks':
        return stocksData;
      case 'tab_commodities':
        return commoditiesData;
      case 'tab_forex':
        return forexData;
      case 'tab_crypto':
        return cryptoData;
      default:
        return [];
    }
  };

  const currentData = getCurrentData();

  return (
    <section className="mb-12">
      {/* Tabs */}
      <div className="flex flex-wrap gap-3 mb-8 border-b border-border pb-4">
        {tabs?.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === tab.id
                ? 'bg-primary text-primary-foreground shadow-lg'
                : 'bg-card text-muted-foreground hover:bg-muted border border-border'
            }`}
          >
            <Icon name={tab.icon as any} size={20} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Data Source Indicator */}
      <div className="mb-4 text-sm text-muted-foreground">
        Data source: {dataSource === 'yahoofinance' ? 'Yahoo Finance API (Live)' : 'Supabase (Fallback)'} • Auto-refresh: 45s
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading market data...</div>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-secondary">Error: {error}</div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">
                    Symbol
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">
                    Name
                  </th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-foreground">
                    Price
                  </th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-foreground">
                    Change
                  </th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-foreground">
                    Volume
                  </th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-foreground">
                    Market Cap
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentData?.map((item) => (
                  <tr
                    key={item.id}
                    className="border-t border-border hover:bg-muted/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <Link 
                        href={`/asset/${item.symbol}`}
                        className="font-mono font-bold text-foreground hover:text-primary transition-colors"
                      >
                        {item.symbol}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <Link 
                        href={`/asset/${item.symbol}`}
                        className="text-foreground hover:text-primary transition-colors"
                      >
                        {item.name}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="font-mono font-semibold text-foreground">
                        ₹{item.price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div
                        className={`flex items-center justify-end gap-1 ${
                          item.change >= 0 ? 'text-primary' : 'text-secondary'
                        }`}
                      >
                        <Icon
                          name={item.change >= 0 ? 'ArrowUpIcon' : 'ArrowDownIcon'}
                          size={16}
                          variant="solid"
                        />
                        <span className="font-mono font-semibold">
                          {Math.abs(item.change).toFixed(2)} ({Math.abs(item.changePercent).toFixed(2)}%)
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="font-mono text-muted-foreground">{item.volume}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="font-mono text-muted-foreground">{item.marketCap}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}